// src/client/renderer/vfx/tile/event-tile-trigger.ts
//
// Plan 16, Task 3.3 — Event tile trigger pool.
//
// Triggered by `LandingEvent` where event.tileType === 'event'.
// Resolves from the landing event's nodeId; position is a placeholder
// ({x:0, y:0, z:0}) because LandingEvent (types.ts:363-368) carries
// `nodeId` and `tileType` but NO world-position — the wire-up site
// (Task 5.1) supplies the real position via manager.spawn(). This
// matches the same pattern as resolveLandingFxForEvent in landing-fx-pool.ts.
//
// Visual choice: sprite-style PlaneGeometry(0.4, 0.4) with a
// MeshBasicMaterial coloured 0xffaa33 (LANDING_FX_VARIANTS.event from Task 3.1).
// We do NOT load a texture or troika Text — pure colour flash avoids
// an async texture load in a one-shot pool. The question-mark glyph
// intent is communicated by the orange colour and float-up animation
// (players associate event tiles with the orange colour already).
//
// Animation timeline (gsap, manual-ticker via GsapRegistry):
//   0.000–0.500s  mesh.position.y: args.position.y → args.position.y + 0.8
//   0.000–0.500s  opacity: 1.0 → 0.0 (proxy drives material.opacity)
// Total lifetime: 500ms. One-shot — isFinished set via tl.call(fn, undefined, 0.5).
// Use tl.call(fn, undefined, abs) — NOT eventCallback (see gsap-registry.ts note).
//
// Max-active: 2 (event tiles are rare; 2 simultaneous landings is the maximum).
//
// Forward-defense F4: pool.dispose() MUST iterate the active set and free
// per-emitter materials + shared geometry + kill all active timelines
// WITHOUT expecting prior pool.release() calls. Idempotent flag guards double-calls.

import * as THREE from 'three';
import type { TileVfxPool, TileVfxSpawnArgs } from './tile-vfx-types';
import type { GsapRegistry } from '../gsap-registry';
import type { GameEvent } from '../../../../shared/game/types';

// ─────────────────────────────────────────────────────────────────────────────
// Animation + geometry constants
// ─────────────────────────────────────────────────────────────────────────────

/** Orange from LANDING_FX_VARIANTS.event (landing-fx-pool.ts Task 3.1). */
const EVENT_TRIGGER_COLOR = 0xffaa33;

const LIFETIME = 0.5; // seconds (500ms)
const FLOAT_RISE = 0.8; // units to float upward

const PLANE_WIDTH = 0.4;
const PLANE_HEIGHT = 0.4;

/** Max concurrent active emitters. 3rd acquire while 2 active returns null. */
const MAX_ACTIVE = 2;

// ─────────────────────────────────────────────────────────────────────────────
// Internal emitter state
// ─────────────────────────────────────────────────────────────────────────────

interface EmitterState {
  handle: ReturnType<GsapRegistry['create']>;
  finished: boolean;
}

// ─────────────────────────────────────────────────────────────────────────────
// Factory
// ─────────────────────────────────────────────────────────────────────────────

export interface EventTileTriggerPoolConfig {
  readonly scene: THREE.Scene;
  readonly registry: GsapRegistry;
}

export function createEventTileTriggerPool({
  scene,
  registry,
}: EventTileTriggerPoolConfig): TileVfxPool {
  // Shared geometry — sprite plane disposed exactly once in pool.dispose()
  const sharedGeometry = new THREE.PlaneGeometry(PLANE_WIDTH, PLANE_HEIGHT);

  // Per-emitter state map: emitter → { handle, finished }
  const emitterStates = new Map<THREE.Object3D, EmitterState>();

  let disposed = false;

  // ── acquire ────────────────────────────────────────────────────────────────

  function acquire(args: TileVfxSpawnArgs): THREE.Object3D | null {
    if (disposed) return null;

    // Enforce max-active cap
    if (emitterStates.size >= MAX_ACTIVE) return null;

    // Per-emitter material clone (additive blend — warm glow on any background)
    const material = new THREE.MeshBasicMaterial({
      color: EVENT_TRIGGER_COLOR,
      transparent: true,
      opacity: 1.0,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
      side: THREE.DoubleSide,
    });

    const mesh = new THREE.Mesh(sharedGeometry, material);
    mesh.position.set(args.position.x, args.position.y, args.position.z);

    scene.add(mesh);

    // Build gsap timeline via registry
    const handle = registry.create('event_tile_trigger');
    const tl = handle.timeline;

    const state: EmitterState = { handle, finished: false };

    // Tween 1: float up 0.8 units over 500ms — proxy drives mesh.position.y
    const posProxy = { value: args.position.y };
    tl.to(
      posProxy,
      {
        value: args.position.y + FLOAT_RISE,
        duration: LIFETIME,
        ease: 'power2.out',
        onUpdate: () => {
          mesh.position.y = posProxy.value;
        },
      },
      0,
    );

    // Tween 2: opacity 1 → 0 over 500ms — proxy drives material.opacity
    const opacityProxy = { value: 1.0 };
    tl.to(
      opacityProxy,
      {
        value: 0.0,
        duration: LIFETIME,
        ease: 'power2.in',
        onUpdate: () => {
          material.opacity = opacityProxy.value;
        },
      },
      0,
    );

    // Completion callback — use tl.call(fn, undefined, absolutePos) per spec.
    // Do NOT use eventCallback('onComplete') — see gsap-registry.ts VIRTUAL_DURATION note.
    tl.call(
      () => {
        state.finished = true;
      },
      undefined,
      LIFETIME,
    );

    emitterStates.set(mesh, state);
    return mesh;
  }

  // ── update ─────────────────────────────────────────────────────────────────
  // The registry's update(dt) drives ALL timelines. This method returns the
  // current active set. Caller (manager) advances dt via registry.update().

  function update(_deltaSeconds: number): ReadonlySet<THREE.Object3D> {
    return new Set(emitterStates.keys());
  }

  // ── isFinished ─────────────────────────────────────────────────────────────

  function isFinished(emitter: THREE.Object3D): boolean {
    const state = emitterStates.get(emitter);
    if (!state) return true; // not tracked — treat as finished
    return state.finished;
  }

  // ── release ────────────────────────────────────────────────────────────────

  function release(emitter: THREE.Object3D): void {
    const state = emitterStates.get(emitter);
    if (!state) return; // safe no-op

    state.handle.dispose(); // kills timeline, removes from registry

    emitterStates.delete(emitter);

    scene.remove(emitter);

    // Dispose per-emitter material
    const mesh = emitter as THREE.Mesh;
    if (mesh.material instanceof THREE.Material) {
      mesh.material.dispose();
    }
  }

  // ── dispose ────────────────────────────────────────────────────────────────
  // Forward-defense F4: iterates active emitters and frees ALL GPU resources
  // WITHOUT expecting prior release() calls. Idempotent.

  function dispose(): void {
    if (disposed) return;
    disposed = true;

    for (const [emitter, state] of emitterStates) {
      state.handle.dispose(); // kill timeline

      scene.remove(emitter);

      // Dispose per-emitter material
      const mesh = emitter as THREE.Mesh;
      if (mesh.material instanceof THREE.Material) {
        mesh.material.dispose();
      }
    }
    emitterStates.clear();

    // Dispose shared geometry exactly once
    sharedGeometry.dispose();
  }

  return { acquire, update, isFinished, release, dispose };
}

// ─────────────────────────────────────────────────────────────────────────────
// Event resolver
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Maps a `LandingEvent` for an `event`-type tile to event-tile-trigger spawn args.
 *
 * Returns null for:
 *   - any non-landing event
 *   - landing events where event.tileType !== 'event'
 *
 * Position note: LandingEvent (src/shared/game/types.ts:363-368) carries `nodeId`
 * and `tileType` but no world-position. The position placeholder {x:0, y:0, z:0}
 * is intentional — the wire-up site (Task 5.1) resolves nodeId→world-pos and
 * passes the real position via manager.spawn(). This matches the pattern used by
 * resolveLandingFxForEvent in landing-fx-pool.ts.
 */
export function resolveEventTileTriggerForEvent(
  event: GameEvent,
): { poolId: 'event_tile_trigger'; args: TileVfxSpawnArgs } | null {
  if (event.type !== 'landing') return null;
  if (event.tileType !== 'event') return null;

  return {
    poolId: 'event_tile_trigger',
    args: {
      position: { x: 0, y: 0, z: 0 }, // wire-up (Task 5.1) supplies real position
      tileType: 'event',
    },
  };
}
